import React, { useState, useRef, useEffect } from "react";
import {
  Fade,
  Typography,
  Box,
  Skeleton,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  CalendarContainer,
  LegendContainer,
  LegendItem,
  LegendColor,
} from "./styles";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import koLocaleFC from "@fullcalendar/core/locales/ko";
import AssessmentIcon from "@mui/icons-material/Assessment";

function CalendarView({
  dataLoading,
  calendarRef,
  calendarEvents,
  dateRange,
  onEventClick,
  onDatesSet,
  selectedItems,
  itemsData,
}) {
  const [calendarKey, setCalendarKey] = useState(1);
  const [calendarApi, setCalendarApi] = useState(null);
  const localCalendarRef = useRef(null);
  useEffect(() => {
    if (localCalendarRef.current) {
      if (calendarRef) {
        calendarRef.current = localCalendarRef.current;
      }
      if (!calendarApi && localCalendarRef.current.getApi) {
        const api = localCalendarRef.current.getApi();
        setCalendarApi(api);
      }
    }
  }, [calendarRef, calendarApi, localCalendarRef]);
  useEffect(() => {
    if (calendarApi) {
      const currentViewStart = calendarApi.view.currentStart;
      const currentViewMonth = currentViewStart.getMonth();
      const currentViewYear = currentViewStart.getFullYear();
      const targetDate = dateRange.start.toDate();
      const targetMonth = targetDate.getMonth();
      const targetYear = targetDate.getFullYear();
      if (currentViewMonth !== targetMonth || currentViewYear !== targetYear) {
        calendarApi.gotoDate(targetDate);
      }
    }
  }, [dateRange, calendarApi]);
  useEffect(() => {
    if (calendarApi && calendarEvents) {
      calendarApi.removeAllEvents();
      calendarApi.addEventSource(calendarEvents);
    }
  }, [calendarEvents, calendarApi]);
  const handleCalendarReady = (calendar) => {
    setCalendarApi(calendar);
  };
  const handleDatesSet = (info) => {
    if (onDatesSet) {
      onDatesSet(info);
    }
  };
  const handleEventClick = (info) => {
    if (onEventClick) {
      onEventClick(info);
    }
  };
  const getSelectedItemNames = () => {
    if (!selectedItems || !itemsData) return "";

    const names = selectedItems.map((id) => {
      const item = itemsData.find((item) => item.id.toString() === id);
      return item ? item.name : id;
    });

    return names.join(", ");
  };
  const selectedItemsStr = getSelectedItemNames();
  return (
    <Fade in={!dataLoading} timeout={500}>
      <CalendarContainer>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6">
            생산계획 달력
            {selectedItemsStr && (
              <Typography
                component="span"
                variant="subtitle1"
                color="text.secondary"
                sx={{ ml: 1, fontWeight: "normal" }}
              >
                ({selectedItemsStr})
              </Typography>
            )}
          </Typography>
          {/* <Tooltip title="상세 분석">
            <IconButton size="small">
              <AssessmentIcon fontSize="small" />
            </IconButton>
          </Tooltip> */}
        </Box>
        <LegendContainer>
          <LegendItem>
            <LegendColor color="#1890ff" />
            <Typography variant="body2">생산계획</Typography>
          </LegendItem>
          <LegendItem>
            <LegendColor color="#52c41a" />
            <Typography variant="body2">입고예정</Typography>
          </LegendItem>
          <LegendItem>
            <LegendColor color="#fa8c16" />
            <Typography variant="body2">출고예정</Typography>
          </LegendItem>
        </LegendContainer>
        {dataLoading ? (
          <Box sx={{ p: 2, height: "calc(100% - 80px)" }}>
            <Skeleton
              variant="rectangular"
              width="100%"
              height="100%"
              animation="wave"
            />
          </Box>
        ) : (
          <FullCalendar
            key={calendarKey}
            ref={localCalendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            headerToolbar={{
              left: "prev,next today",
              center: "title",
              right: "dayGridMonth,timeGridWeek",
            }}
            events={calendarEvents}
            eventClick={handleEventClick}
            datesSet={handleDatesSet}
            initialDate={dateRange.start.toDate()}
            locale={koLocaleFC}
            eventTimeFormat={{
              hour: "numeric",
              minute: "2-digit",
              meridiem: false,
            }}
            height="100%"
          />
        )}
      </CalendarContainer>
    </Fade>
  );
}

export default CalendarView;
